import Head from "next/head";
import { createContext } from "react";
import { Navbar } from "../../components/common/navbar/navbar";
import { Intro } from "../../components/course/intro/intro";
import { FooterSection } from "../../sections/footer/footer";
import { QuestionsSection } from "../../sections/questions/questions";
import { CopyrightSection } from "../../sections/copyright/copyright";
import { ChooseFormSection } from "../../sections/choose-form/choose-form";
import { Details } from "../../components/course/details/details";
import { Description } from "../../components/course/description/description";
import { List } from "../../components/course/list/list";
import { Audience } from "../../components/course/audience/audience";
import { ExpertsSection } from "../../sections/experts/experts";
import { TwitterFeedBackSection } from "../../sections/twitter-feedback/twitter-feedback";
import { Platform } from "../../components/course/platform/platform";
import { DiscordInfo } from "../../components/course/discord-info/discord-info";
import { Programm } from "../../components/course/curriculum/curriculum";
import { CareerBlock } from "../../components/course/career/career";
import { Dao } from "../../components/course/dao/dao";
import { ProjectBlock } from "../../components/course/projects/projects";
import { AfterBootcamp } from "../../components/course/after-bootcamp/after-bootcamp";
import { NftDiploma } from "../../components/course/nft-diploma/nft-diploma";
import { PriceSection } from "../../components/course/price-section/price-section";
import { groq } from "next-sanity";
import { Bootcamp, Bootcamp as BootcampModel } from "../../model/bootcamp";
import { MainPageData } from "../../model/main-page-data";
import { initApiClient } from "../../api/api-client";
import { ScreenshotSection } from "../../sections/screenshot/screenshot";
import {
  GetStaticPropsContext,
  GetStaticPropsResult,
  GetStaticPaths,
} from "next/types";
import { Experts } from "../../model/expert";
import { TwitterFeedback } from "../../model/twitter";
import { Promocode } from "../../model/promocode";
import { InfoAboutBootcamp } from "../../model/info-about-bootcamp";
import { CommonTranslates } from "../../model/common-tranlates";
import { LegalDocuments } from "../../model/legal-documents";
import { BannerWithDiscount } from "../../components/common/banner/banner";
import { useRouter } from "next/router";
import { Screenshot } from "../../model/screenshot";
import { sanitizeImageUrl } from "../../utils/sanity.utils";
import { Banner } from "../../model/banner";
import { FormCmsData } from "../../model/form";
import { TelegramChat } from "../../components/common/telegram-chat/telegram-chat";
import { getCourseStatus } from "../../utils/course-utils";

interface Props {
  bootcamp: BootcampModel;
  mainPageData: MainPageData;
  experts: Experts;
  reviews: TwitterFeedback[];
  promocodes: Promocode[];
  infoAboutBootcamp: InfoAboutBootcamp;
  commonTranslates: CommonTranslates;
  legalDocuments: LegalDocuments;
  screenshots: Screenshot;
  banner: Banner;
  forms: FormCmsData;
}

export const SiteConfig = createContext(0);

function Bootcamp({
  bootcamp,
  reviews,
  experts,
  mainPageData,
  promocodes,
  infoAboutBootcamp,
  commonTranslates,
  legalDocuments,
  screenshots,
  banner,
  forms,
}: Props) {
  const { locale } = useRouter();

  const { isOptimism, isFreeCourses } = getCourseStatus(bootcamp._id);

  return (
    <>
      <Head>
        <title>Guide DAO | Bootcamp</title>
        <meta name="title" content={`${bootcamp.title}`} />
        <meta name="description" content={`${bootcamp.intro}`} />
        <meta property="og:url" content="https://www.guidedao.xyz/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${bootcamp.title}`} />
        <meta property="og:description" content={`${bootcamp.intro}`} />
        <meta
          property="og:image"
          content={sanitizeImageUrl(bootcamp.image.asset._ref)}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="guidedao.xyz" />
        <meta property="twitter:url" content="https://www.guidedao.xyz/" />
        <meta name="twitter:title" content={`${bootcamp.title}`} />
        <meta name="twitter:description" content={`${bootcamp.intro}`} />
        <meta
          name="twitter:image"
          content={sanitizeImageUrl(bootcamp.image.asset._ref)}
        />
      </Head>

      <BannerWithDiscount
        page={"innerPage"}
        forms={forms}
        banner={banner}
        orTitle={commonTranslates.orTitle}
        formId={"Попап с баннера на внутренней страницы"}
        id={bootcamp._id}
      />
      <SiteConfig.Provider value={banner.discount}>
        <Navbar
          menu={infoAboutBootcamp.menu}
          loginBtnTitle={mainPageData.login}
        />
        <TelegramChat />
        <div
          className="pl-5 pr-5 lg:pl-20 lg:pr-20 max-w-[1440px] m-auto xxxxl:pl-0 xxxxl:pr-0"
          id="bootcamp-inner"
        >
          <Intro
            orTitle={commonTranslates.orTitle}
            forms={forms}
            startTitle={commonTranslates.startTitle}
            bootcampTitle={commonTranslates.bootcampTitle}
            ctaTitle={infoAboutBootcamp.ctaTitle}
            info={bootcamp}
            infoAboutBootcamp={infoAboutBootcamp}
          />
          <div className="mt-6 lg:grid grid-cols-4 gap-[10px]">
            {infoAboutBootcamp.features.map((feature, index) => (
              <Details key={index} feature={feature} />
            ))}
          </div>
        </div>

        <div className="pl-5 pr-5 lg:pl-20 lg:pr-20 max-w-[1440px] m-auto xxxxl:pl-0 xxxxl:pr-0">
          <div className="pt-[54px] lg:pt-[86px]">
            <Description
              title={commonTranslates.bootcampTitle}
              theme="dark"
              data={bootcamp.description}
            />
          </div>

          <List data={bootcamp.listOfHighlights} />

          <div className={isFreeCourses ? "pb-50" : ""}>
            <Audience
              data={infoAboutBootcamp.audience}
              title={bootcamp.title}
              id={bootcamp._id}
            />
          </div>
        </div>
        {!isFreeCourses && (
          <ChooseFormSection
            formId={"Форма консультации с внутренней страницы"}
            bootcampName={bootcamp.title}
            title={forms.consultation.title}
            subtitle={forms.consultation.subtitle}
            placeholderEmail={forms.placeholderEmail}
            placeholderTelegram={forms.placeholderTelegram}
            placeholderName={forms.placeholderName}
            button={forms.consultation.button}
            selfContact={forms.selfContact}
            selfContactLink={forms.selfContactLink}
            successScreenTitle={forms.successScreenTitle}
            successScreenSubtitle={forms.successScreenSubtitle}
            dataSendWaitMessage={forms.dataSendWaitMessage}
            alertErrorMessage={forms.alertErrorMessage}
            fillTheFormMessage={forms.fillTheFormMessage}
            orTitle={commonTranslates.orTitle}
          />
        )}
        <div className="bg-white">
          <Platform data={infoAboutBootcamp.platform} />
          <div className="bg-white pl-5 pr-5 lg:pl-20 lg:pr-20 lg:pb-80">
            <div className="max-w-[1440px] m-auto">
              <div className="pt-14 lg:pt-40">
                <Description
                  title="Discord"
                  data={infoAboutBootcamp.processDescription}
                />
              </div>

              <DiscordInfo data={infoAboutBootcamp.communicationPlatform} />
              {locale === "ru" && (
                <div className="pb-24">
                  <ScreenshotSection screenshots={screenshots} />
                </div>
              )}
              <div
                className="lg:ml-10 lg:mr-10 pt-[12px] lg:pt-[52px]"
                aria-label="Experts slides light"
              >
                <ExpertsSection
                  profileTitle={commonTranslates.profileTitle}
                  experts={experts}
                />
              </div>
              <Programm
                showMoreTitle={commonTranslates.showMoreTitle}
                data={bootcamp.curriculum}
              />
              <CareerBlock data={infoAboutBootcamp.career} />
            </div>
          </div>
        </div>
        <div className="relative top-[-45px] m-auto  overflow-hidden">
          <Dao data={infoAboutBootcamp.dao} />
        </div>
        <div className="bg-black">
          <TwitterFeedBackSection reviews={reviews} theme="dark" />
          {!isOptimism && <ProjectBlock data={infoAboutBootcamp.projects} />}
          <AfterBootcamp data={infoAboutBootcamp.companies} />
        </div>
        <div className="bg-white rounded-[30px]">
          <NftDiploma data={infoAboutBootcamp.diploma} />
          <PriceSection
            id={bootcamp._id}
            orTitle={commonTranslates.orTitle}
            startTitle={commonTranslates.startTitle}
            forms={forms}
            highlights={infoAboutBootcamp.highlights}
            title={bootcamp.title}
            ranking={bootcamp.ranking}
            promocodes={promocodes}
            descriptionForPrice={infoAboutBootcamp.descriptionForPrice}
            bootcampTitle={commonTranslates.bootcampTitle}
            oldPriceUsdt={infoAboutBootcamp.oldPriceUsdt}
          />
        </div>
        <div className="bg-blue lg:bg-white relative top-[-45px]">
          <QuestionsSection questions={mainPageData.questions} />
        </div>
        <div className="bg-black bg-cover bg-[left_bottom_20rem] bg-[url('/images/index/footer-bg-mobile.png')] lg:bg-[center_top_-10rem] lg:bg-[url('/images/index/footer-bg-desktop.png')]">
          <CopyrightSection
            orTitle={commonTranslates.orTitle}
            forms={forms}
            data={mainPageData.copyright}
          />
          <FooterSection
            contacts={mainPageData.contacts}
            upButtonTitle={mainPageData.upButtonTitle}
            menu={mainPageData.menu}
            forms={forms}
            agreementTitle={legalDocuments.agreementTitle}
            privacyTitle={legalDocuments.privacyTitle}
          />
        </div>
      </SiteConfig.Provider>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const apiClient = initApiClient();
  const slugsQuery = groq` *[_type == 'bootcamps']`;

  const bootcamps: Bootcamp[] = await apiClient.fetch(slugsQuery);
  const slugs = bootcamps.map((bootcamp) => bootcamp.slug);
  const paths = slugs.flatMap((slug) =>
    (locales ?? []).map((locale) => ({
      params: { slug },
      locale,
    }))
  );

  return { paths, fallback: false };
};

export async function getStaticProps({
  params = {},
  locale,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<Props>> {
  const apiClient = initApiClient();
  const { slug } = params;

  if (!slug) return { notFound: true };

  const localeForLegalDocs = locale === "ru" ? "ru" : "en";

  const query = groq`{
    "bootcamp": *[_type == "bootcamps" && slug == "${slug}" && __i18n_lang == "${locale}"],
    "banner": *[_type == "banner" && __i18n_lang == "${locale}"],
    "mainPageData": *[_type == "mainPage" && __i18n_lang == "${locale}"],
    "experts": *[_type == "experts" && __i18n_lang == "${locale}"],
    "reviews": *[_type == "reviews" && __i18n_lang == "${locale}"],
    "promocodes": *[_type == "promocodes"],
    "commonTranslates": *[_type == "commonTranslates" && __i18n_lang == "${locale}"],
    "infoAboutBootcamp": *[_type == "courses" && __i18n_lang == "${locale}"],
    "legalDocuments": *[_type == "legalDocuments" && __i18n_lang == "${localeForLegalDocs}"],
    "screenshots": *[_type == "screenshots" && __i18n_lang == "ru"],
    "forms": *[_type == "forms" && __i18n_lang == "${locale}"],
  }`;

  const {
    bootcamp: [bootcamp],
    banner: [banner],
    mainPageData: [mainPageData],
    legalDocuments: [legalDocuments],
    experts: [experts],
    screenshots: [screenshots],
    commonTranslates: [commonTranslates],
    infoAboutBootcamp: [infoAboutBootcamp],
    reviews,
    forms: [forms],
    promocodes,
  } = await apiClient.fetch(groq`${query}`);

  if (!bootcamp) return { notFound: true };

  return {
    props: {
      bootcamp: {
        ...bootcamp,
      },
      reviews,
      experts,
      screenshots,
      mainPageData,
      promocodes,
      infoAboutBootcamp,
      commonTranslates,
      legalDocuments,
      forms,
      banner,
    },
    revalidate: 86400,
  };
}

export default Bootcamp;
